import { PointCloudSimpleSubscene } from "@/components/r3f/effects/point-cloud-simple-subscene";
import { CadRenderer } from "@/components/r3f/renderers/cad-renderer";
import { PointCloudRenderer } from "@/components/r3f/renderers/pointcloud-renderer";
import { useSceneBackgroundColor } from "@/components/r3f/utils/scene-background";
import { centerOrthoCamera } from "@/hooks/use-center-camera-on-placeholders";
import { useObjectBoundingBox } from "@/hooks/use-object-bounding-box";
import { useObjectVisibility } from "@/hooks/use-object-visibility";
import { useRealtimeRaycasting } from "@/hooks/use-real-time-raycasting";
import { useCached3DObject } from "@/object-cache";
import {
  selectCloudAndCadAlignReference,
  selectCloudCadAlignmentCloudElevation,
  selectCloudCadAlignmentLayout,
  selectCloudCadAlignmentModelElevation,
  selectCloudCrossSectionEnabled,
  selectIncrementalCloudCadTransform,
  selectModelCrossSectionEnabled,
} from "@/store/modes/cloud-cad-alignment-mode-selectors";
import {
  AlignmentReference,
  setCloudCadIncrementalTransform,
} from "@/store/modes/cloud-cad-alignment-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { isObjPointCloudPoint } from "@/types/threejs-type-guards";
import {
  blue,
  CopyToScreenPass,
  EffectPipelineWithSubScenes,
  FilteredRenderPass,
  Map2DControls,
  quaternionToVector4Tuple,
  TomographicModelPass,
  TwoPointAlignment,
  UniformLights,
  useReproportionCamera,
  View,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericPointCloudStream,
  IElementModel3dStream,
} from "@faro-lotv/ielement-types";
import { assert } from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { Box3, Plane, Quaternion, Vector3 } from "three";
import {
  useCadTomographicMaterial,
  useCameraInCurrentScene,
  useCenterCameraOnBoundingBoxIn2D,
  useClippingPlanesAtCloudElevation,
  useClippingPlanesAtModelElevation,
  useCloudForAlignmentView,
  useNewOrthographicCamera,
} from "../alignment-modes-commons/align-to-cad-utils";
import {
  AlignmentTransform,
  alignmentTransformToMatrix4,
} from "../alignment-modes-commons/alignment-transform";
import { useOverlayElements } from "../overlay-elements-context";
import { RenderDispatch } from "../overview-mode/render-dispatch-utils";

type AlignCloudCadOverlayScreenProps = {
  /** The active sheet to be aligned to the CAD model */
  activeCloud: IElementGenericPointCloudStream;

  /** The active cad model for alignment */
  activeCad: IElementModel3dStream;

  /** The bounding box of the cad model in world */
  cadBox: Box3;
};

/** @returns the overlay scene for align the cloud to cad in 2d */
export function AlignCloudCadOverlayScreen({
  activeCad,
  activeCloud,
  cadBox,
}: AlignCloudCadOverlayScreenProps): JSX.Element {
  const dispatch = useAppDispatch();

  const [controlsEnabled, setControlsEnabled] = useState(true);

  const cadModel = useCached3DObject(activeCad, "AlignCloudCadOverlayScreen");
  const cloudToAlign = useCached3DObject(
    activeCloud,
    "AlignCloudCadOverlayScreen",
  );

  const { singleScreen } = useOverlayElements();
  const background = useSceneBackgroundColor();

  const reference = useAppSelector(selectCloudAndCadAlignReference);

  useObjectVisibility(cadModel, true);

  const incrementalTransform = useAppSelector(
    selectIncrementalCloudCadTransform,
  );
  // The command will apply this transform to the either the cloud or the cad
  // (whichever is being aligned)(), so it can be aligned to the reference in the view
  const [transform] = useState<AlignmentTransform>(
    incrementalTransform ?? {
      position: [0, 0, 0],
      quaternion: [0, 0, 0, 1],
      scale: [1, 1, 1],
    },
  );

  const camera = useNewOrthographicCamera();
  useCameraInCurrentScene(camera);

  const cloudBox = useObjectBoundingBox(cloudToAlign, activeCloud.id);
  assert(cloudBox, "unable to compute bounding box for cloud");

  const boundingBox = useMemo(() => {
    const movingBox =
      reference === AlignmentReference.bimModel
        ? cloudBox.clone()
        : cadBox.clone();
    const referenceBox =
      reference === AlignmentReference.bimModel ? cadBox : cloudBox;
    return movingBox
      .applyMatrix4(alignmentTransformToMatrix4(transform))
      .union(referenceBox);
  }, [cadBox, cloudBox, reference, transform]);

  const size = useThree((s) => s.size);
  const centeringData = useCenterCameraOnBoundingBoxIn2D(
    boundingBox,
    size.width / size.height,
  );
  const [initialCenteringData] = useState(() => centeringData);
  useEffect(() => {
    centerOrthoCamera(camera, initialCenteringData);
  }, [camera, initialCenteringData]);

  useReproportionCamera(camera);
  useCadTomographicMaterial(cadModel);

  const onTransformChanged = useCallback(
    (position: Vector3, quaternion: Quaternion, scale: Vector3) => {
      const transform: AlignmentTransform = {
        scale: scale.toArray(),
        position: position.toArray(),
        quaternion: quaternionToVector4Tuple(quaternion),
      };

      dispatch(setCloudCadIncrementalTransform(transform));
    },
    [dispatch],
  );

  const modelElevation = useAppSelector(selectCloudCadAlignmentModelElevation);
  const cloudElevation = useAppSelector(selectCloudCadAlignmentCloudElevation);

  const isModelCrossSectionEnabled = useAppSelector(
    selectModelCrossSectionEnabled,
  );
  const isCloudCrossSectionEnabled = useAppSelector(
    selectCloudCrossSectionEnabled,
  );

  const modelClippingPlanes = useClippingPlanesAtModelElevation(
    cadModel,
    modelElevation,
    isModelCrossSectionEnabled,
  );
  const computedCloudPlanes = useClippingPlanesAtCloudElevation(
    cloudElevation,
    isCloudCrossSectionEnabled,
  );

  // this segment of the code address re-rendering to apply clipping planes after switch of layout
  const alignmentLayout = useAppSelector(selectCloudCadAlignmentLayout);
  const [cloudClippingPlanes, setCloudClippingPlanes] = useState<Plane[]>([]);
  useEffect(() => {
    setCloudClippingPlanes(computedCloudPlanes);
  }, [computedCloudPlanes, alignmentLayout]);

  useRealtimeRaycasting(cloudToAlign, false);

  useCloudForAlignmentView(cloudToAlign, cloudClippingPlanes);

  return (
    <View
      camera={camera}
      trackingElement={singleScreen}
      background={background}
      hasSeparateScene
    >
      <UniformLights />
      {reference === AlignmentReference.pointCloud && (
        <PointCloudRenderer pointCloud={cloudToAlign} />
      )}
      {reference === AlignmentReference.bimModel && (
        <CadRenderer
          cadModel={cadModel}
          clippingPlanes={modelClippingPlanes}
          clippingInLocalTransform
        />
      )}
      <TwoPointAlignment
        onPointerDown={() => setControlsEnabled(false)}
        onPointerUp={() => setControlsEnabled(true)}
        onTransformChanged={onTransformChanged}
        isScaleEnabled={false}
        disableDataSquash={cloudClippingPlanes.length > 0}
        {...transform}
      >
        {reference === AlignmentReference.bimModel && (
          <PointCloudRenderer pointCloud={cloudToAlign} />
        )}
        {reference === AlignmentReference.pointCloud && (
          <CadRenderer
            cadModel={cadModel}
            clippingPlanes={modelClippingPlanes}
            clippingInLocalTransform
          />
        )}
      </TwoPointAlignment>
      <EffectPipelineWithSubScenes>
        <PointCloudSimpleSubscene pointCloud={cloudToAlign} />
        <TomographicModelPass
          filter={(o) => o.userData.type === RenderDispatch.CadModel}
          camera={camera}
          bias={0.05}
          tomographicColor={blue[600]}
        />
        <FilteredRenderPass
          filter={(o) =>
            !isObjPointCloudPoint(o) &&
            o.userData.type !== RenderDispatch.CadModel
          }
          clear={false}
          clearDepth={false}
        />
        <CopyToScreenPass />
      </EffectPipelineWithSubScenes>
      <Map2DControls
        camera={camera}
        referencePlaneHeight={modelElevation}
        enabled={controlsEnabled}
        isPrimaryControl={false}
      />
    </View>
  );
}
